/**
 * Format data amount with proper decimal places
 */
export const formatDataAmount = (usage: number | null | undefined): string => {
  if (usage === null || usage === undefined || Number.isNaN(usage)) {
    return '0';
  }

  const formatted = usage.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  // Remove .00 if the decimal part is zero
  return formatted.endsWith('.00') ? formatted.slice(0, -3) : formatted;
};

/**
 * Format data usage with appropriate units (GB/TB)
 */
export const formatDataUsage = (usage: number | null | undefined): string => {
  if (usage === null || usage === undefined || Number.isNaN(usage)) {
    return '0 GB';
  }

  if (usage < 1000) {
    return formatDataAmount(usage) + ' GB';
  } else {
    return formatDataAmount(usage / 1000) + ' TB';
  }
};

// Terminal Telemetry Metrics Interface
export enum MetricType {
  throughput = 'throughput',
  latency = 'latency',
  percentage = 'percentage',
  obstruction = 'obstruction',
}

// For other metrics, format with proper decimal places
export const formatMetricValue = (value: number | null | undefined, metricType: MetricType) => {
  if (value == null || value === undefined || Number.isNaN(value)) return '-';
  switch (metricType) {
    case MetricType.latency:
      return `${value.toFixed(0)} ms`; // No decimals for latency
    case MetricType.percentage:
      return `${value.toFixed(0)}%`; // 0 decimals for percentages
    case MetricType.obstruction: {
      const obstructionValue = Math.abs(value) < 1 ? value * 100 : value;
      const rounded = Number(obstructionValue.toFixed(3));
      return rounded > 0 && rounded < 1 ? `${rounded.toFixed(3)}%` : `${obstructionValue.toFixed(0)}%`;
    }
    case MetricType.throughput:
      if (value >= 1000) {
        const mbps = (value / 1000).toFixed(1);
        return `${mbps} Mbps`;
      }
      return `${Math.round(value)} Kbps`;
  }
};

export const sanitize = (name: string): string => {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '-');
};

export function formatNumber(numberArg: number | null | undefined): number {
  if (numberArg === null || numberArg === undefined || Number.isNaN(numberArg)) {
    return 0;
  }

  // Round to 2 decimals numerically — do not use toLocaleString then Number(),
  // because en-US grouping commas (e.g. "7,650.27") parse as NaN.
  return Math.round(numberArg * 100) / 100;
}